// components/NotFound/NotFound.tsx
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import styled from 'styled-components';

// Styled components with theme support
const GlassmorphismBackground = styled(motion.div)<{ themeMode: 'dark' | 'light' }>`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background-color: ${props => props.themeMode === 'dark' 
    ? 'rgba(3, 3, 3, 0.95)' 
    : 'rgba(240, 242, 245, 0.85)'};

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle, 
      ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.15) 0%, rgba(172, 73, 73, 0) 70%' 
        : 'rgba(172, 73, 73, 0.08) 0%, rgba(172, 73, 73, 0) 70%'
      }
    );
    transform: rotate(-12deg);
    z-index: 0;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle, 
      ${props => props.themeMode === 'dark' 
        ? 'rgba(172, 73, 73, 0.1) 0%, rgba(172, 73, 73, 0) 70%' 
        : 'rgba(172, 73, 73, 0.05) 0%, rgba(172, 73, 73, 0) 70%'
      }
    );
    transform: rotate(15deg);
    z-index: 0;
    pointer-events: none;
  }
`;

const GlassCard = styled(motion.div)<{ themeMode: 'dark' | 'light' }>`
  background-color: ${props => props.themeMode === 'dark' 
    ? 'rgba(20, 20, 20, 0.85)' 
    : 'rgba(255, 255, 255, 0.75)'};
  border-radius: 12px;
  box-shadow: ${props => props.themeMode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
    : '0 8px 32px rgba(0, 0, 0, 0.1)'};
  border: 1px solid ${props => props.themeMode === 'dark' 
    ? 'rgba(172, 73, 73, 0.1)' 
    : 'rgba(172, 73, 73, 0.15)'};
  backdrop-filter: blur(10px);
  padding: 3rem;
  max-width: 600px;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const ErrorNumber = styled(motion.div)<{ themeMode: 'dark' | 'light' }>`
  font-size: 8rem;
  font-weight: bold;
  color: ${props => props.themeMode === 'dark' ? '#ac4949' : '#ac4949'};
  text-shadow: ${props => props.themeMode === 'dark' 
    ? '4px 4px 8px rgba(0, 0, 0, 0.3)' 
    : '4px 4px 8px rgba(0, 0, 0, 0.1)'};
`;

const ErrorMessage = styled(motion.h2)<{ themeMode: 'dark' | 'light' }>`
  font-size: 1.8rem;
  color: ${props => props.themeMode === 'dark' ? 'var(--color-text-dark)' : 'var(--color-text-light)'};
  text-align: center;
  margin: 1.5rem 0;
`;

const ErrorDescription = styled(motion.p)<{ themeMode: 'dark' | 'light' }>`
  font-size: 1.1rem;
  color: ${props => props.themeMode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(51, 51, 51, 0.8)'};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const BackButton = styled(motion.button)<{ themeMode: 'dark' | 'light' }>`
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 8px;
  background-color: #ac4949;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #bc5959;
    box-shadow: ${props => props.themeMode === 'dark' 
      ? '0 4px 12px rgba(172, 73, 73, 0.4)' 
      : '0 4px 12px rgba(172, 73, 73, 0.2)'};
  }
`;

const ProgressContainer = styled.div<{ themeMode: 'dark' | 'light' }>`
  width: 100%;
  background-color: ${props => props.themeMode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 5px;
  height: 8px;
  margin: 2rem 0;
  overflow: hidden;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background-color: #ac4949;
  border-radius: 5px;
`;

export const NotFound = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const numberVariants = {
    initial: { scale: 0, rotate: -30 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const iconVariants = {
    initial: { opacity: 0, y: -20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: 0.3,
        duration: 0.5
      }
    },
    float: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

const progressVariants = {
  initial: { width: "0%" },
  animate: { 
    width: ["20%", "80%", "60%", "95%"],
    transition: {
      duration: 4,
      times: [0, 0.4, 0.6, 1],
      repeat: Infinity,
      repeatType: "reverse" as const  // Add "as const" to tell TypeScript this is a literal value
    }
  }
};

  return (
    <GlassmorphismBackground
      themeMode={theme}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <GlassCard
        themeMode={theme}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <motion.div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}
        >
          {[4, 0, 4].map((num, i) => (
            <ErrorNumber
              key={i}
              themeMode={theme}
              variants={numberVariants}
              initial="initial"
              animate="animate"
              transition={{ delay: i * 0.2 }}
            >
              {num}
            </ErrorNumber>
          ))}
        </motion.div>

        <ErrorMessage
          themeMode={theme}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          URL Incorrecta
        </ErrorMessage>

        <ErrorDescription 
          themeMode={theme}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          La dirección que estás intentando visitar no existe o ha sido movida. 
          Nuestros ingenieros están trabajando para mejorar la experiencia.
        </ErrorDescription>

        <div style={{ position: 'relative', width: '100%', textAlign: 'center', margin: '1rem 0' }}>
          <motion.div 
            style={{ display: 'inline-block' }}
            variants={iconVariants}
            initial="initial"
            animate={["animate", "float"]}
          >
            <i 
              className="fas fa-cog" 
              style={{ 
                fontSize: '4rem', 
                color: '#ac4949',
                display: 'inline-block',
                animation: 'spin 6s linear infinite'
              }}
            ></i>
          </motion.div>
          <motion.div 
            style={{ 
              display: 'inline-block', 
              marginLeft: '1rem', 
              position: 'relative', 
              top: '-0.5rem'  
            }}
            variants={iconVariants}
            initial="initial"
            animate={["animate", "float"]}
            transition={{ delay: 0.2 }}
          >
            <i 
              className="fas fa-server" 
              style={{ 
                fontSize: '3.5rem', 
                color: '#ac4949',
                opacity: 0.8
              }}
            ></i>
          </motion.div>
        </div>

        <ProgressContainer themeMode={theme}>
          <ProgressBar
            variants={progressVariants}
            initial="initial"
            animate="animate"
          />
        </ProgressContainer>

        <BackButton
          themeMode={theme}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ delay: 1.2 }}
          onClick={() => {
            navigate(-1);
          }}
        >
          Volver a página anterior
        </BackButton>
      </GlassCard>
    </GlassmorphismBackground>
  );
};

// Add global keyframe for cog animation
const style = document.createElement('style');
style.innerHTML = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
document.head.appendChild(style);